import React, { useState, useEffect } from 'react';
import {
    Search, Eye, Loader, BarChart3, ShieldCheck, Zap, AlertTriangle, FileText, Linkedin, Megaphone,
    Download, Lightbulb, Trash2, Package, LayoutDashboard, Layers, PenSquare, Users, SearchCode, ArrowLeft
} from 'lucide-react';
import apiService from '../../services/apiService';
import { PageLoader } from '../../components/PageLoader';
import StatCard from '../../components/StatCard';

// --- Reusable UI Components ---
const Card = ({ children, className = '' }) => (
    <div className={`bg-slate-800 border border-slate-700 rounded-lg shadow-md ${className}`}>
        {children}
    </div>
);

const Button = ({ children, onClick, className = '', variant = 'primary', icon: Icon, disabled = false }) => {
    const baseClasses = 'flex items-center justify-center gap-2 px-4 py-2 rounded-md font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
    const variants = {
        primary: 'bg-brand-500 text-white hover:bg-brand-400',
        secondary: 'bg-slate-600 text-slate-100 hover:bg-slate-500',
    };
    return (
        <button onClick={onClick} disabled={disabled} className={`${baseClasses} ${variants[variant]} ${className}`}>
            {Icon && <Icon size={16} />}
            {children}
        </button>
    );
};

// --- View for starting a new analysis ---
const InitialView = ({ onAnalyze, history, products, onView, onDelete, error, isLoading }) => {
    const [url, setUrl] = useState('');
    const [selectedProduct, setSelectedProduct] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        onAnalyze(url, selectedProduct || null);
    };
    return (
        <div className="animate-fade-in space-y-6">
            <Card className="p-6">
                 <h2 className="text-xl font-bold text-white mb-1">New Competitor Analysis</h2>
                 <p className="text-slate-400 mb-4 text-sm">Enter a URL and select a product it competes with for a deep, AI-powered analysis.</p>
                 <form onSubmit={handleSubmit} className="space-y-4">
                     <div className="flex flex-col sm:flex-row gap-2">
                         <input
                             type="url"
                             value={url}
                             onChange={(e) => setUrl(e.target.value)}
                             placeholder="https://competitor.com"
                             className="flex-grow bg-slate-700 border border-slate-600 text-white text-sm rounded-lg p-3 focus:ring-2 focus:ring-brand-500"
                             required
                         />
                         <select
                             value={selectedProduct}
                             onChange={(e) => setSelectedProduct(e.target.value)}
                             className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg p-3 focus:ring-2 focus:ring-brand-500 sm:w-1/3"
                         >
                             <option value="">No specific product</option>
                             {products.map(p => <option key={p._id} value={p._id}>{p.title}</option>)}
                         </select>
                         <Button type="submit" disabled={isLoading} icon={isLoading ? Loader : Search}>Analyze</Button>
                     </div>
                     {error && <p className="text-red-400 text-sm mt-2 text-left">{error}</p>}
                 </form>
            </Card>
            <Card className="p-6">
                 <h2 className="text-xl font-bold text-white mb-4">Previous Analyses</h2>
                 {Object.entries(history).map(([productName, reports]) => (
                    <div key={productName} className="mb-8 last:mb-0">
                         <h3 className="text-xl font-semibold text-brand-400 flex items-center gap-2 mb-3">
                             <Package size={20} /> {productName}
                         </h3>
                         <div className="bg-slate-800/50 border border-slate-700 rounded-lg">
                             <ul className="divide-y divide-slate-700">
                                 {reports.map(item => (
                                     <li key={item._id} onClick={() => onView(item._id)} className="p-4 flex justify-between items-center hover:bg-slate-800 cursor-pointer transition-colors">
                                         <div className="flex items-center gap-4">
                                             <div className="p-3 bg-slate-700 rounded-lg"><BarChart3 className="text-brand-400" /></div>
                                             <div>
                                                 <p className="font-bold text-white">{item.name}</p>
                                                 <p className="text-sm text-slate-400">Analyzed on {new Date(item.createdAt).toLocaleDateString()}</p>
                                             </div>
                                         </div>
                                         {/* --- FIXED: Reverted onClick to original state --- */}
                                         <button onClick={(e) => onDelete(e, item._id)} className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-900/50 rounded-full transition-colors">
                                             <Trash2 size={16} />
                                         </button>
                                     </li>
                                 ))}
                             </ul>
                         </div>
                    </div>
                ))}
            </Card>
        </div>
    );
};

// --- View for displaying analysis results ---
const AnalysisDetailView = ({ analysis, onBack, isLoading }) => {
    const handleExportPdf = () => alert("PDF export functionality would be implemented here.");
    const renderContent = (content) => (typeof content === 'string' || typeof content === 'number') ? content : '[Complex data]';

    const ReportSection = ({ title, icon, children }) => (
        <Card className="p-6">
            <h3 className="text-xl font-bold text-white flex items-center gap-3 mb-4">
                <span className="text-brand-400">{icon}</span>
                {title}
            </h3>
            <div className="text-slate-300">{children}</div>
        </Card>
    );

    const SwotCard = ({ title, items, icon, color }) => (
        <div className="bg-slate-900/50 p-6 rounded-lg h-full">
            <h4 className={`text-lg font-bold flex items-center gap-2 mb-3 ${color}`}>{icon} {title}</h4>
            <ul className="space-y-2 list-disc pl-5 text-slate-300 text-sm">
                {(items || []).map((item, index) => <li key={index}>{renderContent(item)}</li>)}
            </ul>
        </div>
    );

    const KeywordsTable = ({ keywords }) => (
        <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead className="text-xs text-slate-400 uppercase bg-slate-900/50">
                    <tr><th className="p-4">Keyword</th><th className="p-4">Difficulty</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                    {(keywords || []).map((kw, index) => (
                        <tr key={index}><td className="p-4 font-medium text-white">{renderContent(kw.keyword)}</td><td className="p-4"><div className="flex items-center gap-2"><span className="text-white font-semibold">{renderContent(kw.difficulty)}</span><div className="w-full bg-slate-700 rounded-full h-2.5"><div className="bg-brand-500 h-2.5 rounded-full" style={{ width: `${kw.difficulty}%` }}></div></div></div></td></tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
            {isLoading && <PageLoader text="Loading report..." />}
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h2 className="text-3xl font-bold text-white">Analysis: <a href={analysis.url} target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:underline">{analysis.name}</a></h2>
                    <p className="text-slate-400">Report generated on {new Date().toLocaleDateString()}</p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={onBack} variant="secondary" icon={ArrowLeft}>Back</Button>
                    <Button onClick={handleExportPdf} icon={Download}>Export</Button>
                </div>
            </header>
            <div className="animate-fade-in space-y-8">
                <ReportSection title="Company Overview" icon={<FileText />}><p className="text-slate-300">{renderContent(analysis.companyOverview)}</p></ReportSection>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <StatCard title="Est. Monthly Visits" value={renderContent(analysis.traffic?.monthlyVisits) || 'N/A'} icon={<BarChart3 />} />
                    <StatCard title="Domain Authority" value={renderContent(analysis.traffic?.domainAuthority) || 'N/A'} icon={<ShieldCheck />} />
                </div>
                <ReportSection title="SWOT Analysis" icon={<Zap />}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <SwotCard title="Strengths" items={analysis.swot?.strengths || []} icon={<Zap />} color="text-green-400" />
                        <SwotCard title="Weaknesses" items={analysis.swot?.weaknesses || []} icon={<AlertTriangle />} color="text-yellow-400" />
                        <SwotCard title="Opportunities" items={analysis.swot?.opportunities || []} icon={<Lightbulb />} color="text-cyan-400" />
                        <SwotCard title="Threats" items={analysis.swot?.threats || []} icon={<ShieldCheck />} color="text-red-400" />
                    </div>
                </ReportSection>
                <ReportSection title="Top Keywords" icon={<FileText />}><KeywordsTable keywords={analysis.topKeywords || []} /></ReportSection>
                <ReportSection title="Marketing & Ads Strategy" icon={<Megaphone />}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div>
                            <h4 className="text-lg font-bold text-white flex items-center gap-2 mb-3"><Linkedin /> Social Media</h4>
                            <div className="space-y-4">
                                {(analysis.socialMedia || []).map((s, i) => (<div key={i}><h5 className="font-bold text-white">{renderContent(s.platform)}</h5><p className="text-sm text-slate-400 mb-2">{renderContent(s.strategy)}</p><blockquote className="border-l-4 border-slate-600 pl-4 italic text-sm text-slate-300">"{renderContent(s.examplePost)}"</blockquote></div>))}
                            </div>
                        </div>
                        <div>
                            <h4 className="text-lg font-bold text-white flex items-center gap-2 mb-3"><Megaphone /> Paid Ads</h4>
                            <div className="space-y-4">
                                {(analysis.paidAds || []).map((ad, i) => (<div key={i} className="bg-slate-800 p-4 rounded-lg"><h5 className="font-bold text-brand-400">{renderContent(ad.headline)}</h5><p className="text-sm text-slate-300 mt-1">{renderContent(ad.description)}</p></div>))}
                            </div>
                        </div>
                    </div>
                </ReportSection>
            </div>
        </div>
    );
};


// --- Main Controller Component (The single source of truth for this file) ---
export default function CompetitorAnalysisView ({ aiCommand, clearAiCommand, setActiveView, setFoundryAssets }) {
    const [currentAnalysis, setCurrentAnalysis] = useState(null);
    const [analysisHistory, setAnalysisHistory] = useState({});
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchProducts = async () => {
        try {
            const hubData = await apiService.fetchBrandHub();
            setProducts(hubData.products || []);
        } catch (err) {
            console.error("Could not load products.", err);
        }
    };

    const fetchHistory = async () => {
        try {
            const history = await apiService.fetchCompetitorAnalysisHistory();
            setAnalysisHistory(history);
        } catch (err) {
            console.error("Could not load analysis history.", err);
        }
    };

    const handleAnalyze = async (urlToAnalyze, productId) => {
        if (!urlToAnalyze) { setError('Please enter a valid URL.'); return; }
        setIsLoading(true);
        setError('');
        setCurrentAnalysis(null);
        try {
            const result = await apiService.runCompetitorAnalysis(urlToAnalyze, productId);
            setCurrentAnalysis(result.reportData);
            if (result && result.reportData) {
                const newPublishedAsset = {
                    id: result.reportData._id || `analysis-${Date.now()}`,
                    title: `Competitor Report: ${result.reportData.name}`,
                    from: 'Competitor Analysis',
                    icon: Users
                };
                setFoundryAssets(prevAssets => ({ ...prevAssets, published: [newPublishedAsset, ...prevAssets.published] }));
            }
            await fetchHistory();
        } catch (err) {
            setError(err.message || 'Failed to analyze competitor.');
            setCurrentAnalysis(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchHistory();
        fetchProducts();
        if (aiCommand && aiCommand.view === 'foundryCompetitorAnalysis' && aiCommand.payload?.autoRun) {
            const { url, productId } = aiCommand.payload;
            handleAnalyze(url, productId);
            clearAiCommand();
        }
    }, [aiCommand, clearAiCommand]);

    const handleBack = () => {
        setCurrentAnalysis(null);
    };
    
    const handleViewHistory = async (reportId) => {
        setIsLoading(true);
        try {
            const fullReport = await apiService.fetchCompetitorAnalysisReport(reportId);
            setCurrentAnalysis(fullReport.reportData);
        } catch(err) {
            setError("Could not load the selected report.");
        } finally {
            setIsLoading(false);
        }
    };
    
    // VERIFIED AND CORRECTED FUNCTION
    const handleDeleteHistory = async (e, reportId) => {
        // Stop the click from bubbling up to the parent li's onClick
        e.stopPropagation(); 

        if (window.confirm("Are you sure you want to delete this analysis?")) {
            try {
                // This console.log is useful for debugging the ID
                console.log("Attempting to delete report with ID:", reportId);
                
                await apiService.deleteCompetitorAnalysisReport(reportId);
                
                // Refresh the history list after a successful deletion
                await fetchHistory(); 
            } catch(err) {
                console.error("Delete failed:", err);
                alert(`Failed to delete the report: ${err.message}`);
            }
        }
    };

    const navItems = [
        { id: 'foundry', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'campaigns', label: 'Campaign Builder', icon: Layers },
        { id: 'foundryContentCreation', label: 'Content Creator', icon: PenSquare },
        { id: 'foundryCompetitorAnalysis', label: 'Competitor Analysis', icon: Users },
        { id: 'seoAudit', label: 'SEO Audit', icon: SearchCode },
    ];
    
    return (
        <div className="p-6">
            <header className="flex flex-col md:flex-row justify-between md:items-center gap-4 border-b border-slate-700 pb-4 mb-6">
                <h1 className="text-3xl font-bold text-white">Foundry</h1>
                <div className="bg-slate-800 p-1 rounded-lg flex items-center gap-1 flex-wrap">
                    {navItems.map(item => (
                        <button
                            key={item.id}
                            onClick={() => setActiveView(item.id)}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors duration-200 ${
                                'foundryCompetitorAnalysis' === item.id ? 'bg-[#0f766e] text-white' : 'text-slate-300 hover:bg-slate-700'
                            }`}
                        >
                            <item.icon size={16} />
                            {item.label}
                        </button>
                    ))}
                </div>
            </header>

            {currentAnalysis ? (
                <AnalysisDetailView analysis={currentAnalysis} onBack={handleBack} isLoading={isLoading} />
            ) : (
                <InitialView 
                    initialUrl={aiCommand?.payload?.url || ''}
                    onAnalyze={handleAnalyze} 
                    history={analysisHistory}
                    products={products}
                    onView={handleViewHistory} 
                    onDelete={handleDeleteHistory} 
                    error={error} 
                    isLoading={isLoading} 
                />
            )}
        </div>
    );
};